import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { CreditCard, FileText, Hash } from 'lucide-react';
import Slider from '../common/Slider.jsx';
import { calculateInstallments, formatCurrency } from '../../utils/calculations';
import { useSalesContext } from '../../contexts/SalesContext.jsx';

const FinancialSimulator = ({ productValue, onSimulationComplete }) => {
  const { register, control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      downPaymentPercentage: 30,
      installments: 36,
      paymentMethod: 'credit',
      applyDiscount: true,
      campaignId: 1
    }
  });
  
  const { campaigns } = useSalesContext();
  const [simulation, setSimulation] = useState(null);
  
  // Valores observados para cálculos em tempo real
  const downPaymentPercentage = watch('downPaymentPercentage');
  const installmentsCount = watch('installments');
  const applyDiscount = watch('applyDiscount');
  const campaignId = watch('campaignId');
  
  // Recalcular quando valores mudarem
  useEffect(() => {
    const selectedCampaign = campaigns.find(c => c.id === parseInt(campaignId));
    const discount = (applyDiscount && selectedCampaign) ? 
      productValue * (selectedCampaign.discountPercentage / 100) : 0;
    
    const totalValue = productValue - discount;
    const downPaymentValue = totalValue * (downPaymentPercentage / 100);
    const financedAmount = totalValue - downPaymentValue;
    
    const installmentValue = calculateInstallments(
      financedAmount, 
      installmentsCount,
      3.5 // Taxa de juros mensal
    );
    
    setSimulation({
      totalValue,
      discount,
      downPaymentValue,
      financedAmount,
      installmentValue,
      installmentsCount,
      selectedCampaign
    });
  }, [productValue, downPaymentPercentage, installmentsCount, applyDiscount, campaignId, campaigns]);
  
  const onSubmit = (data) => {
    onSimulationComplete({
      ...data,
      simulation
    });
  };
  
  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Simulador Financeiro</h2>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Informações do Produto */}
        <div className="mb-6 p-4 bg-blue-50 rounded-md">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Valor do produto:</span>
            <span className="font-medium">{formatCurrency(productValue)}</span>
          </div>
          
          {simulation?.discount > 0 && (
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Desconto campanha:</span>
              <span className="font-medium text-red-600">
                -{formatCurrency(simulation.discount)}
              </span>
            </div>
          )}
          
          <div className="border-t border-blue-200 pt-2 flex justify-between">
            <span className="font-bold text-gray-800">Valor total:</span>
            <span className="font-bold text-blue-900">
              {formatCurrency(simulation?.totalValue || productValue)}
            </span>
          </div>
        </div>
        
        {/* Entrada */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entrada ({downPaymentPercentage}%)
          </label>
          <Controller
            name="downPaymentPercentage"
            control={control}
            render={({ field }) => (
              <Slider
                min={10}
                max={50}
                step={5}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-500">
              {downPaymentPercentage}% de entrada
            </span>
            <span className="font-medium">
              {formatCurrency(simulation?.downPaymentValue || 0)}
            </span>
          </div>
        </div>
        
        {/* Parcelas */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número de Parcelas
          </label>
          <div className="grid grid-cols-4 gap-2">
            {[12, 24, 36, 48, 60, 72, 84, 96].map(num => (
              <label
                key={num}
                className={`py-2 px-3 text-center rounded cursor-pointer text-sm
                  ${installmentsCount === num 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <input
                  type="radio"
                  className="sr-only"
                  value={num}
                  {...register("installments")}
                />
                {num}x
              </label>
            ))}
          </div>
          
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-500">
              {installmentsCount} parcelas de
            </span>
            <span className="font-medium">
              {formatCurrency(simulation?.installmentValue || 0)}
            </span>
          </div>
        </div>
        
        {/* Forma de Pagamento */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Forma de Pagamento
          </label>
          <div className="grid grid-cols-3 gap-3">
            {['credit', 'boleto', 'pix'].map(method => (
              <label
                key={method}
                className={`
                  flex flex-col items-center justify-center p-3 border rounded
                  ${watch('paymentMethod') === method 
                    ? 'bg-blue-50 border-blue-300' 
                    : 'border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  type="radio"
                  className="sr-only"
                  value={method}
                  {...register("paymentMethod")}
                />
                <div className="text-center">
                  {method === 'credit' && <CreditCard className="mx-auto h-6 w-6 mb-2" />}
                  {method === 'boleto' && <FileText className="mx-auto h-6 w-6 mb-2" />}
                  {method === 'pix' && <Hash className="mx-auto h-6 w-6 mb-2" />}
                  <span className="text-sm">
                    {method === 'credit' && 'Cartão de Crédito'}
                    {method === 'boleto' && 'Boleto Bancário'}
                    {method === 'pix' && 'PIX'}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
        
        {/* Aplicar Campanha */}
        <div className="mb-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="applyDiscount"
              className="h-4 w-4 text-blue-900 focus:ring-blue-500 border-gray-300 rounded"
              {...register("applyDiscount")}
            />
            <label htmlFor="applyDiscount" className="ml-2 block text-sm text-gray-700">
              Aplicar campanha promocional
            </label>
          </div>
          
          {applyDiscount && (
            <div className="mt-3">
              <select
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                {...register("campaignId")}
              >
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.discountPercentage}%)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        {/* Botões */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            onClick={() => {
              setValue('downPaymentPercentage', 30);
              setValue('installments', 36);
              setValue('paymentMethod', 'credit');
              setValue('applyDiscount', true);
              setValue('campaignId', 1);
            }}
          >
            Reiniciar
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-900 hover:bg-blue-800"
          >
            Confirmar Condições
          </button>
        </div>
      </form>
    </div>
  );
};

export default FinancialSimulator;